import { createApi } from '@reduxjs/toolkit/query/react';

import { agentChatSlice } from '../slices/agentChat';
import {
  AgentChatAttachment,
  AgentChatMessage,
  AgentChatThread,
  CreateAgentChatMessageRequest,
  CreateAgentChatThread,
  MaterializedCitation,
  TAgentChatOrchestratorStatus,
  TMaterializedCitation,
  UpdateAgentChatThread,
} from '../types/agent-chat-types';
import { baseQueryWithErrorHandling } from './baseQuery';

// Types for run status and resume functionality
export interface AgentChatRunDetails {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  stream_available: boolean;
}

export interface RunStatusResponse {
  has_active_run: boolean;
  run: AgentChatRunDetails | null;
}

export const agentChatApiClient = createApi({
  reducerPath: 'agentChatApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'AgentChatThreads',
    'AgentChatWorkspaces',
    'AgentChatMessages',
    'AgentChatAttachments',
    'AgentChatCitations',
  ] as const,
  endpoints: (builder) => ({
    // Thread endpoints
    listAgentChatThreads: builder.query<
      AgentChatThread[],
      { matterId: string; workspaceId?: string; coverageId?: string }
    >({
      query: ({ matterId, workspaceId, coverageId }) => {
        const params = new URLSearchParams();
        if (workspaceId) params.set('workspace_id', workspaceId);
        if (coverageId) params.set('coverage_id', coverageId);
        const queryString = params.toString();
        return `api/matters/${matterId}/agent-threads/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, { matterId }) => [
        { type: 'AgentChatThreads', id: matterId },
      ],
    }),

    createAgentChatThread: builder.mutation<
      AgentChatThread,
      { matterId: string; data: CreateAgentChatThread }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/agent-threads/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'AgentChatThreads', id: matterId },
      ],
    }),

    getAgentChatThread: builder.query<
      AgentChatThread,
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/`,
      providesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatThreads', id: `${matterId}:${threadId}` },
      ],
    }),

    updateAgentChatThread: builder.mutation<
      AgentChatThread,
      { matterId: string; threadId: string; data: UpdateAgentChatThread }
    >({
      query: ({ matterId, threadId, data }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatThreads', id: `${matterId}:${threadId}` },
        { type: 'AgentChatThreads', id: matterId },
      ],
    }),

    deleteAgentChatThread: builder.mutation<
      void,
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'AgentChatThreads', id: matterId },
      ],
    }),

    suggestThreadName: builder.mutation<
      { suggested_name: string },
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/suggest_name/`,
        method: 'POST',
      }),
    }),

    // Message endpoints
    listAgentChatMessages: builder.query<
      AgentChatMessage[],
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/messages/`,
      providesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatMessages', id: `${matterId}:${threadId}` },
      ],

      async onQueryStarted({ threadId }, { queryFulfilled, dispatch }) {
        const result = await queryFulfilled;
        dispatch(
          agentChatSlice.actions.messageListReplaced({
            threadId,
            messages: result.data,
          }),
        );
      },
    }),

    runAgentChatMessage: builder.mutation<
      void,
      {
        matterId: string;
        threadId: string;
        data: CreateAgentChatMessageRequest;
      }
    >({
      query: ({ matterId, threadId, data }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/messages/run/`,
        method: 'POST',
        body: data,
      }),
      // This endpoint returns a streaming response, so we don't invalidate tags here
      // The UI should handle updating the state using the streaming response
    }),

    // Attachment endpoints
    listAgentChatAttachments: builder.query<
      AgentChatAttachment[],
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/attachments/`,
      providesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatAttachments', id: `${matterId}:${threadId}` },
      ],
    }),

    createAgentChatAttachment: builder.mutation<
      AgentChatAttachment,
      { matterId: string; threadId: string; data: FormData }
    >({
      query: ({ matterId, threadId, data }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/attachments/`,
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatAttachments', id: `${matterId}:${threadId}` },
      ],
    }),

    getAgentChatAttachment: builder.query<
      AgentChatAttachment,
      { matterId: string; threadId: string; attachmentId: string }
    >({
      query: ({ matterId, threadId, attachmentId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/attachments/${attachmentId}/`,
      providesTags: (result, error, { matterId, threadId, attachmentId }) => [
        {
          type: 'AgentChatAttachments',
          id: `${matterId}:${threadId}:${attachmentId}`,
        },
      ],
    }),

    deleteAgentChatAttachment: builder.mutation<
      void,
      { matterId: string; threadId: string; attachmentId: string }
    >({
      query: ({ matterId, threadId, attachmentId }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/attachments/${attachmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatAttachments', id: `${matterId}:${threadId}` },
      ],
    }),

    // Citation endpoints
    getAgentChatCitation: builder.query<
      TMaterializedCitation,
      { matterId: string; citationId: string; workspaceId?: string }
    >({
      query: ({ matterId, citationId, workspaceId }) =>
        `api/matters/${matterId}/agent-citations/${citationId}/$${''}`.replace(
          '$',
          workspaceId ? `?workspace_id=${workspaceId}` : '',
        ),
      providesTags: (result, error, { matterId, citationId }) => [
        {
          type: 'AgentChatCitations',
          id: `${matterId}:${citationId}`,
        },
      ],
      transformResponse: (response: TMaterializedCitation) => {
        return MaterializedCitation.parse(response);
      },
    }),

    getAgentChatOrchestratorStatus: builder.query<
      TAgentChatOrchestratorStatus,
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/agent-orchestrator/`,
    }),

    // Workspace endpoints
    listAgentChatWorkspaces: builder.query<
      { id: string; name: string }[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/agent-chat-workspaces/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'AgentChatWorkspaces', id: matterId },
      ],
    }),

    // Run status endpoint for checking active runs (used for stream resumption)
    getRunStatus: builder.query<
      RunStatusResponse,
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) =>
        `api/matters/${matterId}/agent-threads/${threadId}/messages/status/`,
    }),

    // Cancel active run endpoint (for temporal streaming mode)
    cancelRun: builder.mutation<
      { success: boolean; deleted_message_count: number },
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/messages/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatMessages', id: `${matterId}:${threadId}` },
      ],
    }),

    // V2: Stop active run (cancels generation, deletes run messages)
    stopRun: builder.mutation<
      { success: boolean; deleted_message_count: number },
      { matterId: string; threadId: string }
    >({
      query: ({ matterId, threadId }) => ({
        url: `api/matters/${matterId}/agent-threads/${threadId}/messages/stop/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, threadId }) => [
        { type: 'AgentChatMessages', id: `${matterId}:${threadId}` },
      ],
    }),
  }),
});

export const {
  useListAgentChatThreadsQuery,
  useCreateAgentChatThreadMutation,
  useGetAgentChatThreadQuery,
  useUpdateAgentChatThreadMutation,
  useDeleteAgentChatThreadMutation,
  useSuggestThreadNameMutation,
  useListAgentChatMessagesQuery,
  useRunAgentChatMessageMutation,
  useListAgentChatAttachmentsQuery,
  useCreateAgentChatAttachmentMutation,
  useGetAgentChatAttachmentQuery,
  useDeleteAgentChatAttachmentMutation,
  useGetAgentChatCitationQuery,
  useGetAgentChatOrchestratorStatusQuery,
  useListAgentChatWorkspacesQuery,
  useGetRunStatusQuery,
  useLazyGetRunStatusQuery,
  useCancelRunMutation,
  useStopRunMutation,
} = agentChatApiClient;
